/**
 * Clever Instant Login return (CLEVER_SPEC §3). Clever redirects here with
 * `?code=` (a portal-tile login also lands here with no state); the code is
 * exchanged server-side for a custom token and the user is signed in.
 *
 * Certification asks for an idempotent exchange (a duplicate callback must
 * not exchange twice), a new Clever login to replace any existing session
 * (shared devices), and a friendly error screen with a way out.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { Alert, Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import { auth } from '../firebase';
import { exchangeCleverCode } from '../services/cloudFunctions';

const CODE_KEY = 'athleto.clever.code';

export default function CleverCallback() {
  const { t } = useTranslation('team');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState(t('cleverCallback.signingIn'));
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const code = params.get('code') || '';
    const problem = params.get('error') || '';
    if (problem) {
      setError(t('cleverCallback.errorWithCode', { code: problem }));
      return;
    }
    if (!code) {
      setError(t('cleverCallback.noCode'));
      return;
    }
    let alreadyUsed = false;
    try {
      alreadyUsed = sessionStorage.getItem(CODE_KEY) === code;
      sessionStorage.setItem(CODE_KEY, code);
    } catch {
      /* storage unavailable: proceed, the server-side exchange is single use anyway */
    }
    if (alreadyUsed) {
      // A duplicate callback (back button, double effect): do not exchange again.
      navigate(auth.currentUser ? '/' : '/login', { replace: true });
      return;
    }

    (async () => {
      try {
        if (auth.currentUser) {
          setStatus(t('cleverCallback.switchingAccounts'));
          await auth.signOut();
        }
        const result = await exchangeCleverCode(code);
        setStatus(t('cleverCallback.almostThere'));
        const credential = await signInWithCustomToken(auth, result.token);
        await credential.user.getIdToken(true);
        navigate('/', { replace: true });
      } catch (e: any) {
        setError(e?.message || t('cleverCallback.signInFailed'));
      }
    })();
  }, [params, navigate]);

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        {error ? (
          <>
            <Typography variant="h5" gutterBottom>
              {t('cleverCallback.title')}
            </Typography>
            <Alert severity="error" sx={{ textAlign: 'left', my: 2 }}>
              {error}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('cleverCallback.tryAgainHint')}
            </Typography>
            <Button component={RouterLink} to="/login" variant="contained">
              {t('cleverCallback.backToSignIn')}
            </Button>
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body1">{status}</Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
}
